import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  EnterpriseScreenShell, actionButtonFactory, useCan, useStandardConfirms, useUnsavedChangesGuard,
} from 'components/enterprise-shell';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, ColumnSelector, SyutGrid, SyutSearchGrid, SyutSelect,
  type ActionMenuItem, type ColumnDefinition, type SyutGridColumn, type SyutSearchGridColumn,
} from 'components/ui';
import GridLoadingSkeleton from 'components/common/GridLoadingSkeleton';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import {
  apiErrorMessage,
  type TaxMasterApi,
  type TaxMasterBasisOption,
  type TaxMasterPage,
  type TaxMasterQuery,
  type TaxMasterSummary,
  type TaxMasterVersion,
} from './taxMaster.types';
import './tax-master.css';
import TaxMasterFormModal, { type TaxEditorMode } from './TaxMasterFormModal';
import TaxMasterVersionsModal from './TaxMasterVersionsModal';

/**
 * ============================================================================
 * TAX MASTERS — relocatable shared screen (ADR-0019 §8, ADR-0022 §3)
 * ============================================================================
 * The maintenance surface for user-configured tax masters: list, version
 * timeline, create / copy / append-version / amend-future / delete-future.
 *
 * Follows the enterprise screen blueprint end to end: `EnterpriseScreenShell`
 * (header icon from the catalog, search, list/card view toggle, filter panel with
 * one field per column, column selector, F2 search popup, gated Add / Export),
 * `SyutGrid` with server-side paging, permission-gated row actions, delete through
 * `useStandardConfirms`, `EnterpriseFormModal` for the editor and the canonical
 * view modal for the version timeline — both minimizable.
 *
 * Relocation contract (see README.md): copy this folder into any Enterprise-
 * Template app, supply `api` + `jurisdictionCode` (+ the pack's component codes
 * and bases), register in the app's navigation. All governance rules live
 * SERVER-SIDE in the API template Tax module; a refusal's message is the user
 * guidance and is surfaced verbatim.
 * ============================================================================
 */

export interface TaxMastersScreenProps {
  tabId?: string;
  /** Icon key from the catalog menu row (the layout passes it to every screen). */
  moduleIcon?: string;
  /** Tab title — the minimize strip's module label. */
  moduleName?: string;
  api: TaxMasterApi;
  jurisdictionCode: string;
  componentCodes: string[];
  /** Published slab bases from the pack — the editor's "Evaluates against" options. */
  bases?: TaxMasterBasisOption[];
  /** Passed straight to the editor — see its `supplyCategories` prop for why ZERO is conditional. */
  supplyCategories?: Array<{ value: string; label: string }>;
  /**
   * The catalog SCREEN CODE this app's `useCan` resolves against — `app.tbl_app_ui_screen.screen_code`,
   * seeded as TAX_MASTERS by database-template seed/019 and gated server-side as UI.TAX_MASTERS.*.
   * A host whose catalog uses another code passes it here. A code with no catalog row matches
   * nothing and silently hides every button, so this is host-supplied, never guessed.
   */
  permissionResource?: string;
}

interface EditorState {
  mode: TaxEditorMode;
  rateCode?: string | null;
  base?: TaxMasterVersion | null;
  summary?: TaxMasterSummary | null;
}

interface DetailState {
  summary: TaxMasterSummary;
  versions: TaxMasterVersion[];
}

type ViewMode = 'list' | 'grid';
type GridRecord = TaxMasterSummary & { id: string };

const DEFAULT_SUPPLY_CATEGORIES = [
  { value: 'STANDARD', label: 'Standard' },
  { value: 'EXEMPT', label: 'Nil / Exempt' },
];

const COLUMN_STORAGE_KEY = 'tax-masters-columns';

// 'select' and 'actions' are structural: required rows cannot be hidden, so the row-action
// menu can never disappear (workspace rule "SyutGrid row-actions visibility").
const columnDefinitions: ColumnDefinition[] = [
  { id: 'select',        label: '',              required: true,  defaultVisible: true,  width: 50, sortable: false },
  { id: 'name',          label: 'Name',          required: false, defaultVisible: true,  sortable: true },
  { id: 'code',          label: 'Code',          required: false, defaultVisible: true,  sortable: true },
  { id: 'kind',          label: 'Type',          required: false, defaultVisible: true,  sortable: true },
  { id: 'category',      label: 'Category',      required: false, defaultVisible: false, sortable: true },
  { id: 'rate',          label: 'Rate',          required: false, defaultVisible: true,  sortable: true },
  { id: 'versions',      label: 'Versions',      required: false, defaultVisible: true,  sortable: true },
  { id: 'inForce',       label: 'In force from', required: false, defaultVisible: true,  sortable: true },
  { id: 'next',          label: 'Next change',   required: false, defaultVisible: true,  sortable: true },
  { id: 'actions',       label: 'Actions',       required: true,  defaultVisible: true,  width: 80, sortable: false },
];

const defaultVisibleColumns = () => columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id);

const EMPTY_PAGE: TaxMasterPage = {
  items: [], page: 1, pageSize: 25, totalCount: 0, totalPages: 0, hasNext: false, hasPrevious: false,
};

const dateOnly = (v?: string | null) => (v ? v.slice(0, 10) : '');

const KindBadges: React.FC<{ m: TaxMasterSummary }> = ({ m }) => (
  <>
    <span className={`txm-badge txm-badge-${m.kind.toLowerCase()}`}>{m.kind}</span>
    {m.hasCess && <span className="txm-badge txm-badge-cess">+cess</span>}
  </>
);

export const TaxMastersScreen: React.FC<TaxMastersScreenProps> = ({
  tabId, moduleIcon, moduleName = 'Tax Masters', api, jurisdictionCode, componentCodes, bases, supplyCategories,
  permissionResource = 'TAX_MASTERS',
}) => {
  // `useCan` is typed to each app's own screen-code union in some hosts and to `string` in
  // others. The module cannot import that union without breaking relocatability, so the
  // host-supplied code is widened to whatever this app's signature accepts — at this one call.
  const can = useCan(permissionResource as Parameters<typeof useCan>[0]);
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();
  const categories = supplyCategories ?? DEFAULT_SUPPLY_CATEGORIES;

  // ---- server-side list state ------------------------------------------------------------
  const [queryParams, setQueryParams] = useState<TaxMasterQuery>({ page: 1, pageSize: 25 });
  const [page, setPage] = useState<TaxMasterPage>(EMPTY_PAGE);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPage(await api.list(jurisdictionCode, queryParams));
    } catch (err) {
      notificationService.toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [api, jurisdictionCode, queryParams]);

  useEffect(() => { void load(); }, [load]);

  // Search is server-side: debounce, then push into the query (blueprint).
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams((prev) => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // ---- view / columns --------------------------------------------------------------------
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(COLUMN_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as string[];
        return ['select', 'actions', ...parsed.filter((c) => c !== 'select' && c !== 'actions')];
      }
    } catch { /* fall through to defaults */ }
    return defaultVisibleColumns();
  });
  const handleApplyColumns = useCallback((cols: string[]) => {
    const next = ['select', 'actions', ...cols.filter((c) => c !== 'select' && c !== 'actions')];
    setVisibleColumns(next);
    try { localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  }, []);
  const handleResetColumns = useCallback(() => {
    setVisibleColumns(defaultVisibleColumns());
    try { localStorage.removeItem(COLUMN_STORAGE_KEY); } catch { /* ignore */ }
  }, []);

  // ---- filter panel: one field per grid column (blueprint) ---------------------------------
  const [filterOpen, setFilterOpen] = useState(false);
  const [kindFilter, setKindFilter] = useState('');
  const [cessFilter, setCessFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [inForceFromFilter, setInForceFromFilter] = useState('');
  const [inForceToFilter, setInForceToFilter] = useState('');
  const [nextChangeFromFilter, setNextChangeFromFilter] = useState('');
  const [nextChangeToFilter, setNextChangeToFilter] = useState('');
  const [scheduledFilter, setScheduledFilter] = useState('');

  const filterValues = [
    kindFilter, cessFilter, categoryFilter, inForceFromFilter, inForceToFilter,
    nextChangeFromFilter, nextChangeToFilter, scheduledFilter,
  ];
  const appliedFilterCount = [
    queryParams.kind, queryParams.hasCess, queryParams.supplyCategory, queryParams.inForceFrom,
    queryParams.inForceTo, queryParams.nextChangeFrom, queryParams.nextChangeTo, queryParams.hasScheduledChange,
  ].filter((v) => v !== undefined && v !== '').length;

  // Screen-level unsaved-changes guard: an open filter panel with values is work worth keeping.
  useUnsavedChangesGuard({ isDirty: filterOpen && filterValues.some(Boolean) });

  const applyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      kind: kindFilter || undefined,
      hasCess: cessFilter === '' ? undefined : cessFilter === 'yes',
      supplyCategory: categoryFilter || undefined,
      inForceFrom: inForceFromFilter || undefined,
      inForceTo: inForceToFilter || undefined,
      nextChangeFrom: nextChangeFromFilter || undefined,
      nextChangeTo: nextChangeToFilter || undefined,
      hasScheduledChange: scheduledFilter === '' ? undefined : scheduledFilter === 'yes',
      page: 1,
    }));
    setFilterOpen(false);
  }, [kindFilter, cessFilter, categoryFilter, inForceFromFilter, inForceToFilter, nextChangeFromFilter, nextChangeToFilter, scheduledFilter]);

  const clearFilters = useCallback(() => {
    setKindFilter(''); setCessFilter(''); setCategoryFilter('');
    setInForceFromFilter(''); setInForceToFilter('');
    setNextChangeFromFilter(''); setNextChangeToFilter(''); setScheduledFilter('');
    setQueryParams((prev) => ({ page: 1, pageSize: prev.pageSize, searchTerm: prev.searchTerm }));
  }, []);

  // ---- modals ----------------------------------------------------------------------------
  const [detail, setDetail] = useState<DetailState | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [busy, setBusy] = useState(false);
  const [searchPopupOpen, setSearchPopupOpen] = useState(false);

  // Slot-reuse rather than a monotonic counter: the (N) exists only to tell parked Adds apart in
  // the minimized strip, so it must stay small rather than climbing all session.
  const [createInstanceId, setCreateInstanceId] = useState('1');
  const formMinimizeRef = useRef<(() => void) | null>(null);

  const rows = page.items;
  const findRow = useCallback((rateCode: string) => rows.find((m) => m.rateCode === rateCode) ?? null, [rows]);

  const openDetail = useCallback(async (summary: TaxMasterSummary) => {
    const viewId = tabId ? `${tabId}-view-modal-${summary.rateCode}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId);
      modal?.onRestore?.();
      return;
    }
    try {
      setDetail({ summary, versions: await api.get(jurisdictionCode, summary.rateCode) });
    } catch (err) {
      notificationService.toast.error(apiErrorMessage(err));
    }
  }, [api, jurisdictionCode, tabId, isMinimized, getMinimizedModal, dismissMinimized]);

  const latestVersion = (versions: TaxMasterVersion[]) => versions[versions.length - 1] ?? null;
  const futureVersion = (versions: TaxMasterVersion[]) => versions.find((v) => v.isFuture) ?? null;

  const handleAddNew = useCallback(() => {
    let next = 1;
    while (tabId && isMinimized(`${tabId}-form-modal-create-${next}`)) next += 1;
    setCreateInstanceId(String(next));
    setEditor({ mode: 'create' });
  }, [isMinimized, tabId]);

  /** Copy is record-keyed: a minimized Copy of this master is restored, never duplicated. */
  const handleCopy = useCallback(async (summary: TaxMasterSummary) => {
    const modalId = tabId ? `${tabId}-form-modal-copy-${summary.rateCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    try {
      const versions = await api.get(jurisdictionCode, summary.rateCode);
      setEditor({ mode: 'copy', rateCode: summary.rateCode, base: latestVersion(versions), summary });
    } catch (err) {
      notificationService.toast.error(apiErrorMessage(err));
    }
  }, [api, jurisdictionCode, tabId, isMinimized, getMinimizedModal, dismissMinimized]);

  const handleNewVersion = useCallback(async (summary: TaxMasterSummary) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${summary.rateCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    try {
      const versions = await api.get(jurisdictionCode, summary.rateCode);
      setEditor({ mode: 'append', rateCode: summary.rateCode, base: latestVersion(versions), summary });
    } catch (err) {
      notificationService.toast.error(apiErrorMessage(err));
    }
  }, [api, jurisdictionCode, tabId, isMinimized, getMinimizedModal, dismissMinimized]);

  const handleAmendScheduled = useCallback(async (summary: TaxMasterSummary) => {
    try {
      const versions = await api.get(jurisdictionCode, summary.rateCode);
      const future = futureVersion(versions);
      if (!future) {
        notificationService.toast.info('This master has no scheduled version to amend.');
        return;
      }
      setEditor({ mode: 'replace', rateCode: summary.rateCode, base: future, summary });
    } catch (err) {
      notificationService.toast.error(apiErrorMessage(err));
    }
  }, [api, jurisdictionCode]);

  const deleteVersion = useCallback(async (summary: TaxMasterSummary, version: TaxMasterVersion) => {
    setBusy(true);
    try {
      await api.deleteVersion(jurisdictionCode, summary.rateCode, version.effectiveFrom, 'removed via Tax Masters screen');
      notificationService.toast.success(`Scheduled version of "${summary.name}" deleted.`);
      await load();
      if (detail?.summary.rateCode === summary.rateCode) {
        setDetail({ summary, versions: await api.get(jurisdictionCode, summary.rateCode) });
      }
    } catch (err) {
      notificationService.toast.error(apiErrorMessage(err), 8000);
    } finally {
      setBusy(false);
    }
  }, [api, detail, jurisdictionCode, load]);

  /** Row-level delete: the scheduled (future) version, after the standard confirm. */
  const handleDeleteScheduled = useCallback(async (summary: TaxMasterSummary) => {
    try {
      const versions = await api.get(jurisdictionCode, summary.rateCode);
      const future = futureVersion(versions);
      if (!future) {
        notificationService.toast.info('This master has no scheduled version to delete. Versions in force are immutable.');
        return;
      }
      const ok = await confirmDelete({ resourceLabel: `the version of "${summary.name}" scheduled for ${dateOnly(future.effectiveFrom)}` });
      if (!ok) return;
      await deleteVersion(summary, future);
    } catch (err) {
      notificationService.toast.error(apiErrorMessage(err));
    }
  }, [api, jurisdictionCode, confirmDelete, deleteVersion]);

  const handleSaved = useCallback(async () => {
    const reopen = detail?.summary;
    setEditor(null);
    await load();
    if (reopen) {
      try { setDetail({ summary: reopen, versions: await api.get(jurisdictionCode, reopen.rateCode) }); } catch { /* list refreshed; detail stays */ }
    }
  }, [api, detail, jurisdictionCode, load]);

  const handleRestoreModal = useCallback((
    mode: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string,
  ) => {
    if (instanceId) setCreateInstanceId(instanceId);
    const summary = recordCode ? findRow(recordCode) : null;
    // A restored version editor needs its master back in context; a restored create does not.
    setEditor((prev) => prev ?? {
      mode: mode === 'copy' ? 'copy' : recordCode ? 'append' : 'create',
      rateCode: recordCode ?? null,
      summary,
    });
  }, [findRow]);

  const handleRestoreView = useCallback(async (rateCode: string) => {
    const summary = findRow(rateCode);
    if (!summary) return;
    try {
      setDetail({ summary, versions: await api.get(jurisdictionCode, rateCode) });
    } catch (err) {
      notificationService.toast.error(apiErrorMessage(err));
    }
  }, [api, findRow, jurisdictionCode]);

  // F2 opens the search popup, as on every list screen.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' && !searchPopupOpen && !editor && !detail) {
        e.preventDefault();
        setSearchPopupOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchPopupOpen, editor, detail]);

  // ---- export ----------------------------------------------------------------------------
  const canExport = can('EXPORT') && typeof api.exportCsv === 'function';
  const handleExport = useCallback(async () => {
    if (!api.exportCsv) return;
    try {
      const blob = await api.exportCsv(jurisdictionCode, queryParams);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tax-masters-${jurisdictionCode}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      notificationService.toast.success('Export downloaded.');
    } catch (err) {
      notificationService.toast.error(apiErrorMessage(err));
    }
  }, [api, jurisdictionCode, queryParams]);

  // ---- grid ------------------------------------------------------------------------------
  const columns = useMemo<SyutGridColumn<GridRecord>[]>(() => [
    { id: 'name', header: 'Name', accessorKey: 'name', size: 240 },
    { id: 'code', header: 'Code', accessorKey: 'rateCode', size: 150, cell: ({ row }) => <span className="txm-mono">{row.original.rateCode}</span> },
    { id: 'kind', header: 'Type', accessorKey: 'kind', size: 130, cell: ({ row }) => <KindBadges m={row.original} /> },
    { id: 'category', header: 'Category', accessorKey: 'supplyCategory', size: 120 },
    { id: 'rate', header: 'Rate', accessorKey: 'rateSummary', size: 110, cell: ({ row }) => <span className="txm-mono">{row.original.rateSummary}</span> },
    { id: 'versions', header: 'Versions', accessorKey: 'versionCount', size: 90 },
    { id: 'inForce', header: 'In force from', accessorKey: 'inForceFrom', size: 130, cell: ({ row }) => <span>{dateOnly(row.original.inForceFrom) || '—'}</span> },
    {
      id: 'next', header: 'Next change', accessorKey: 'nextChangeOn', size: 130,
      cell: ({ row }) => row.original.nextChangeOn
        ? <span className="txm-badge txm-badge-future">{dateOnly(row.original.nextChangeOn)}</span>
        : <span className="txm-dim">—</span>,
    },
  ], []);

  // Row actions, permission-gated. View is always available; the mutating actions map the
  // master's real lifecycle: versions in force are immutable, so "edit" and "delete" act on
  // the SCHEDULED (future) version and appear only when one exists.
  const getRowActions = useCallback((record: GridRecord): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => { void openDetail(record); } },
    ];
    if (can('UPDATE')) {
      items.push({ id: 'new-version', label: 'New Version', icon: <IconSystem.add size={18} />, onClick: () => { void handleNewVersion(record); } });
      if (record.nextChangeOn) {
        items.push({ id: 'amend', label: 'Amend Scheduled Version', icon: <IconSystem.edit size={18} />, onClick: () => { void handleAmendScheduled(record); } });
      }
    }
    if (can('CREATE')) {
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => { void handleCopy(record); } });
    }
    if (can('DELETE') && record.nextChangeOn) {
      items.push({ id: 'delete', label: 'Delete Scheduled Version', icon: <IconSystem.trash size={18} />, onClick: () => { void handleDeleteScheduled(record); }, variant: 'danger' });
    }
    return items;
  }, [can, openDetail, handleNewVersion, handleAmendScheduled, handleCopy, handleDeleteScheduled]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Card View', tooltip: 'Card View' },
  ];

  const actionButtons: ActionButton[] = [
    actionButtonFactory.search(() => setSearchPopupOpen(true)),
    { ...actionButtonFactory.add(handleAddNew, 'Add New Tax Master'), visible: can('CREATE') },
    actionButtonFactory.refresh(() => { void load(); }),
    { ...actionButtonFactory.export_(() => { void handleExport(); }, 'Export CSV'), visible: canExport },
  ];

  const searchColumns: SyutSearchGridColumn<TaxMasterSummary>[] = [
    { key: 'rateCode', header: 'Code', width: '140px', sortable: true, searchable: true },
    { key: 'name', header: 'Name', width: '220px', sortable: true, searchable: true },
    { key: 'kind', header: 'Type', width: '100px', sortable: true, render: (_v: string, m: TaxMasterSummary) => <KindBadges m={m} /> },
    { key: 'rateSummary', header: 'Rate', width: '90px', sortable: true },
    { key: 'inForceFrom', header: 'In force from', width: '120px', sortable: true, render: (v: string | null) => dateOnly(v) || '—' },
  ];

  const gridData = useMemo<GridRecord[]>(() => rows.map((m) => ({ ...m, id: m.rateCode })), [rows]);
  const currentPage = queryParams.page ?? 1;
  const currentPageSize = queryParams.pageSize ?? 25;
  const totalPages = Math.max(1, page.totalPages || 1);

  return (
    <EnterpriseScreenShell
      title="Tax Masters"
      description="User-configured tax rates: flat, slab and cess — versioned by effective date"
      icon={moduleIcon && (IconSystem as Record<string, React.FC<{ size?: number }>>)[moduleIcon]
        ? React.createElement((IconSystem as Record<string, React.FC<{ size?: number }>>)[moduleIcon], { size: 18 })
        : React.createElement(IconSystem.percent, { size: 18 })}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search name or code…' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode as ViewMode) }}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount: appliedFilterCount }}
      customToolbarItems={
        <ColumnSelector
          columns={columnDefinitions}
          visibleColumns={visibleColumns}
          onApply={handleApplyColumns}
          onReset={handleResetColumns}
        />
      }
      trailingContent={filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button type="button" className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Type"
                options={[{ value: 'FLAT', label: 'Flat' }, { value: 'SLAB', label: 'Slab' }]}
                value={kindFilter}
                onChange={(val) => setKindFilter((val as string) || '')}
                placeholder="All types"
                isClearable
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Cess"
                options={[{ value: 'yes', label: 'With cess' }, { value: 'no', label: 'Without cess' }]}
                value={cessFilter}
                onChange={(val) => setCessFilter((val as string) || '')}
                placeholder="All"
                isClearable
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Category"
                options={categories}
                value={categoryFilter}
                onChange={(val) => setCategoryFilter((val as string) || '')}
                placeholder="All categories"
                isClearable
                size="sm"
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>In force from (on or after)</label>
              <input type="date" className="form-input" value={inForceFromFilter} onChange={(e) => setInForceFromFilter(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>In force from (on or before)</label>
              <input type="date" className="form-input" value={inForceToFilter} onChange={(e) => setInForceToFilter(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Next change (on or after)</label>
              <input type="date" className="form-input" value={nextChangeFromFilter} onChange={(e) => setNextChangeFromFilter(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Next change (on or before)</label>
              <input type="date" className="form-input" value={nextChangeToFilter} onChange={(e) => setNextChangeToFilter(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Scheduled change"
                options={[{ value: 'yes', label: 'Has a scheduled version' }, { value: 'no', label: 'No scheduled version' }]}
                value={scheduledFilter}
                onChange={(val) => setScheduledFilter((val as string) || '')}
                placeholder="All"
                isClearable
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button type="button" className="btn btn-secondary" onClick={clearFilters}>Clear</button>
            <button type="button" className="btn btn-primary" onClick={applyFilters}>Apply</button>
          </div>
        </div>
      )}
      ConfirmDialogPortal={ConfirmDialogPortal}
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          loading && rows.length === 0 ? (
            <GridLoadingSkeleton rows={10} columns={visibleColumns.length || 6} />
          ) : (
            <SyutGrid
              data={gridData}
              columns={columns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={page.totalCount}
              currentPage={currentPage - 1}
              pageSize={currentPageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              isLoading={loading}
              rowActions={getRowActions}
              visibleColumns={visibleColumns}
              emptyMessage="No tax masters found"
            />
          )
        )}

        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="txm-cards">
              {gridData.length === 0 ? (
                <div className="txm-cards-empty">
                  <IconSystem.grid size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                  <p>{loading ? 'Loading…' : 'No tax masters found'}</p>
                </div>
              ) : gridData.map((record) => (
                <div key={record.id} className="txm-card">
                  <div className="txm-card-head">
                    <div>
                      <div className="txm-card-code txm-mono">{record.rateCode}</div>
                      <h3 className="txm-card-title">{record.name}</h3>
                    </div>
                    <ActionMenu items={getRowActions(record)} />
                  </div>
                  <div><KindBadges m={record} /></div>
                  <div className="txm-card-facts">
                    <div><div className="txm-card-fact-label">Rate</div><div className="txm-card-fact-value txm-mono">{record.rateSummary}</div></div>
                    <div><div className="txm-card-fact-label">Category</div><div className="txm-card-fact-value">{record.supplyCategory}</div></div>
                    <div><div className="txm-card-fact-label">In force from</div><div className="txm-card-fact-value">{dateOnly(record.inForceFrom) || '—'}</div></div>
                    <div><div className="txm-card-fact-label">Next change</div><div className="txm-card-fact-value">{dateOnly(record.nextChangeOn) || '—'}</div></div>
                  </div>
                </div>
              ))}
            </div>
            {gridData.length > 0 && (
              <div className="pagination-footer">
                <div className="pagination-info">Showing {gridData.length} of {page.totalCount} items</div>
                <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <select value={currentPageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))} className="form-input" style={{ width: 'auto', padding: '0.375rem 0.5rem', fontSize: '0.85rem' }}>
                    {[10, 25, 50, 100].map((size) => <option key={size} value={size}>{size} / page</option>)}
                  </select>
                  <button type="button" className="page-btn" onClick={() => handlePageChange(0)} disabled={currentPage <= 1}><IconSystem.chevronLeft size={16} /><IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} /></button>
                  <button type="button" className="page-btn" onClick={() => handlePageChange(currentPage - 2)} disabled={currentPage <= 1}><IconSystem.chevronLeft size={16} /></button>
                  <span style={{ padding: '0 0.5rem', fontSize: '0.85rem' }}>Page {currentPage} of {totalPages}</span>
                  <button type="button" className="page-btn" onClick={() => handlePageChange(currentPage)} disabled={currentPage >= totalPages}><IconSystem.chevronRight size={16} /></button>
                  <button type="button" className="page-btn" onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages}><IconSystem.chevronRight size={16} /><IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} /></button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {detail && !editor && (
        <TaxMasterVersionsModal
          isOpen
          summary={detail.summary}
          versions={detail.versions}
          busy={busy}
          canUpdate={can('UPDATE')}
          canDelete={can('DELETE')}
          tabId={tabId}
          moduleName={moduleName}
          onRestoreView={(code) => { void handleRestoreView(code); }}
          onAmend={(v) => setEditor({ mode: 'replace', rateCode: detail.summary.rateCode, base: v, summary: detail.summary })}
          onNewVersion={() => setEditor({
            mode: 'append', rateCode: detail.summary.rateCode, base: latestVersion(detail.versions), summary: detail.summary,
          })}
          onDelete={(v) => { void deleteVersion(detail.summary, v); }}
          onClose={() => setDetail(null)}
        />
      )}

      {editor && (
        <TaxMasterFormModal
          isOpen
          editorMode={editor.mode}
          api={api}
          jurisdictionCode={jurisdictionCode}
          componentCodes={componentCodes}
          bases={bases}
          supplyCategories={categories}
          rateCode={editor.rateCode ?? null}
          baseVersion={editor.base ?? null}
          baseSummary={editor.summary ?? null}
          tabId={tabId}
          moduleName={moduleName}
          createInstanceId={createInstanceId}
          minimizeRef={formMinimizeRef}
          onClose={() => setEditor(null)}
          onSaved={handleSaved}
          onRestoreModal={handleRestoreModal}
        />
      )}

      <SyutSearchGrid<TaxMasterSummary>
        isOpen={searchPopupOpen}
        onClose={() => setSearchPopupOpen(false)}
        onSelect={(m) => { setSearchPopupOpen(false); void openDetail(m); }}
        title="Search Tax Masters"
        columns={searchColumns}
        loadData={async ({ searchQuery: q, filters, sortKey, sortDirection, page: p, pageSize }) => {
          const res = await api.list(jurisdictionCode, {
            page: p, pageSize, searchTerm: q || undefined, kind: filters.kind || undefined,
            sortBy: sortKey, sortDescending: sortDirection === 'desc',
          });
          return { data: res.items, total: res.totalCount };
        }}
        rowKey="rateCode"
        filters={[{ key: 'kind', label: 'Type', type: 'select', options: [{ value: 'FLAT', label: 'Flat' }, { value: 'SLAB', label: 'Slab' }] }]}
        enableRecentlyUsed
        recentlyUsedKey="tax-masters-search"
        searchPlaceholder="Search by code or name… (F2 to focus)"
      />
    </EnterpriseScreenShell>
  );
};

export default TaxMastersScreen;
